import React, { useState, useRef, useEffect, useCallback } from 'react';
import { opportunitiesApi } from '../api/client';
import Far12Form from './Far12Form';
import Far13Form from './Far13Form';
import Far14Form from './Far14Form';
import Far15Form from './Far15Form';
import { requestAiDraft, requestAiReview, buildOppContext, AiField, AiReview } from './aiAssist';

/* ============================================================================
 * Solicitation & Acquisition Method — Tab 1: Acquisition Path
 * Determines commerciality (FAR 12) and selects Part 13 / 14 / 15.
 * Saved to opportunity.acquisition_data (JSON). Other sub-tabs (Solicitation
 * Development, Responses & Evaluation, Selection & Award) reuse the existing
 * Draft / Matches / Evaluate tools, reached via the sub-tab bar.
 * ==========================================================================*/

// Simplified Acquisition thresholds — CONFIGURABLE (edit here when FAR changes).
const THRESHOLDS = {
  micro_purchase: 10000,
  simplified_acquisition: 250000,   // SAT
  commercial_sat: 7500000,          // FAR 13.5 commercial SAT ceiling
};

interface Props {
  opp: any;
  oppId: string;
  onSaved?: (acq: any) => void;
  goToSubTab?: (tabId: string) => void;
}

const st = {
  section: { background: 'white', borderRadius: 8, border: '1px solid #e5e7eb', padding: 20, marginBottom: 16 } as React.CSSProperties,
  title: { fontSize: 15, fontWeight: 700, color: '#1f2937', margin: '0 0 4px' } as React.CSSProperties,
  sub: { fontSize: 11, color: '#6b7280', margin: '0 0 16px' } as React.CSSProperties,
  label: { fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 } as React.CSSProperties,
  req: { color: '#dc2626' } as React.CSSProperties,
  input: { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' } as React.CSSProperties,
  field: { marginBottom: 14 } as React.CSSProperties,
  help: { fontSize: 10, color: '#9ca3af', margin: '2px 0 0' } as React.CSSProperties,
};

export default function AcquisitionTab({ opp, oppId, onSaved, goToSubTab }: Props) {
  const initial = (() => {
    try { const raw = opp?.acquisition_data; return raw ? (typeof raw === 'string' ? JSON.parse(raw) : raw) : {}; } catch { return {}; }
  })();

  const [data, setData] = useState<Record<string, any>>(initial);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [dirty, setDirty] = useState(false);
  const dirtyRef = useRef(false);
  const dataRef = useRef(data);
  useEffect(() => { dataRef.current = data; }, [data]);

  const markDirty = () => { if (!dirtyRef.current) { dirtyRef.current = true; setDirty(true); } };
  const set = (k: string, v: any) => { markDirty(); setData(prev => ({ ...prev, [k]: v })); };
  const get = (k: string, def: any = '') => (data[k] !== undefined ? data[k] : def);

  // Bound get/set for the FAR Part 12 slice (stored under data.far12).
  const far12Get: (k: string, def?: any) => any = (k, def = '') => {
    const slice = data.far12 || {};
    return slice[k] !== undefined ? slice[k] : def;
  };
  const far12Set = (k: string, v: any) => { markDirty(); setData(prev => ({ ...prev, far12: { ...(prev.far12 || {}), [k]: v } })); };

  const far13Get: (k: string, def?: any) => any = (k, def = '') => {
    const slice = data.far13 || {};
    return slice[k] !== undefined ? slice[k] : def;
  };
  const far13Set = (k: string, v: any) => { markDirty(); setData(prev => ({ ...prev, far13: { ...(prev.far13 || {}), [k]: v } })); };

  const far14Get: (k: string, def?: any) => any = (k, def = '') => {
    // Section 1 suitability reads from the top-level path answers too.
    if (k === 'discussions_unnecessary' && (data.far14 || {}).discussions_unnecessary === undefined) {
      return data.discussions_needed === 'No' ? 'Yes' : data.discussions_needed === 'Yes' ? 'No' : '';
    }
    if (k === 'price_related_only' && (data.far14 || {}).price_related_only === undefined) {
      return data.price_only || '';
    }
    const slice = data.far14 || {};
    return slice[k] !== undefined ? slice[k] : def;
  };
  const far14Set = (k: string, v: any) => { markDirty(); setData(prev => ({ ...prev, far14: { ...(prev.far14 || {}), [k]: v } })); };

  const far15Get: (k: string, def?: any) => any = (k, def = '') => {
    // Discussions answer defaults from the Acquisition Path question.
    if (k === 'will_discussions' && (data.far15 || {}).will_discussions === undefined) {
      return data.discussions_needed || '';
    }
    const slice = data.far15 || {};
    return slice[k] !== undefined ? slice[k] : def;
  };
  const far15Set = (k: string, v: any) => { markDirty(); setData(prev => ({ ...prev, far15: { ...(prev.far15 || {}), [k]: v } })); };

  const save = useCallback(async () => {
    setSaving(true); setError(''); setMessage('');
    try {
      await opportunitiesApi.update(oppId, { acquisition_data: JSON.stringify(data) });
      setMessage('Acquisition path saved.');
      dirtyRef.current = false; setDirty(false);
      onSaved?.(data);
    } catch (e: any) { setError(e.message || 'Save failed.'); }
    setSaving(false);
  }, [data, oppId, onSaved]);

  useEffect(() => {
    const h = (e: BeforeUnloadEvent) => { if (dirtyRef.current) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, []);

  // ─── AI Assist (advisory drafting for the Acquisition Path) ───
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState<Record<string, any> | null>(null);
  const [aiError, setAiError] = useState('');
  const ACQ_AI_FIELDS: AiField[] = [
    { key: 'commerciality_determination', label: 'Commerciality Determination rationale', kind: 'long' },
    { key: 'selection_rationale', label: 'Method Selection Rationale', kind: 'long' },
  ];
  const runAiAssist = async () => {
    setAiLoading(true); setAiError(''); setAiSuggestions(null);
    try {
      const ctx = buildOppContext(opp) +
        `\n\nEstimated value: ${get('estimated_value')}\nCommercial: ${get('is_commercial')} (${get('commercial_classification')})` +
        `\nRecommended method: ${recommended}\nCO-selected method: ${get('co_selected_method')}`;
      const out = await requestAiDraft('Acquisition Method Selection (commerciality + Part 13/14/15 rationale)', ACQ_AI_FIELDS, ctx);
      if (Object.keys(out).length === 0) setAiError('AI did not return usable suggestions. Try again.');
      else setAiSuggestions(out);
    } catch (e: any) { setAiError(e.message || 'AI Assist failed.'); }
    setAiLoading(false);
  };
  const applyAll = () => { if (aiSuggestions) { Object.keys(aiSuggestions).forEach(k => set(k, aiSuggestions[k])); setAiSuggestions(null); } };

  // ─── AI Review (compliance / completeness) ───
  const [reviewLoading, setReviewLoading] = useState(false);
  const [review, setReview] = useState<AiReview | null>(null);
  const ACQ_CHECKLIST = [
    'Commerciality determination is documented (if commercial, FAR 12 applies).',
    'Estimated value and applicable threshold are recorded.',
    'The selected procedure (Part 13/14/15) is appropriate for the value and competition approach.',
    'Sealed bidding (Part 14) is only used when discussions are unnecessary and award is price-based.',
    'Selection rationale is documented.',
    'Evaluation method/factors are defined for the chosen path.',
    'Requirements do not unnecessarily restrict competition.',
  ];
  const runAiReview = async () => {
    setReviewLoading(true); setReview(null); setAiError('');
    try {
      const ctx = buildOppContext(opp) + '\n\nAcquisition data:\n' + JSON.stringify(data).slice(0, 8000);
      const r = await requestAiReview('Acquisition Method Selection (FAR 12-15)', ACQ_CHECKLIST, ctx);
      setReview(r);
    } catch (e: any) { setAiError(e.message || 'AI Review failed.'); }
    setReviewLoading(false);
  };

  // ─── System calculations ────────────────────────────────────────────────
  const value = parseFloat(String(get('estimated_value')).replace(/[^0-9.]/g, '')) || 0;
  const isCommercial = get('is_commercial') === 'Yes' ||
    !['Noncommercial product or service', 'Undetermined', ''].includes(get('commercial_classification'));

  let thresholdCategory = '';
  let recommended = '';
  if (value > 0) {
    if (value <= THRESHOLDS.micro_purchase) { thresholdCategory = 'At or below Micro-Purchase Threshold'; recommended = 'FAR Part 13 — Simplified Acquisition'; }
    else if (value <= THRESHOLDS.simplified_acquisition) { thresholdCategory = 'At or below Simplified Acquisition Threshold (SAT)'; recommended = 'FAR Part 13 — Simplified Acquisition'; }
    else if (isCommercial && value <= THRESHOLDS.commercial_sat) { thresholdCategory = 'Commercial items — within FAR 13.5 SAT ceiling'; recommended = 'FAR Part 13 — Simplified Acquisition (13.5)'; }
    else { thresholdCategory = 'Above Simplified Acquisition Threshold'; recommended = 'FAR Part 15 — Contracting by Negotiation'; }
    // If sealed-bid conditions are met, prefer Part 14
    if (get('discussions_needed') === 'No' && get('price_only') === 'Yes' && value > THRESHOLDS.simplified_acquisition) {
      recommended = 'FAR Part 14 — Sealed Bidding';
    }
  }
  // Persist computed recommendation so badges elsewhere can read it.
  useEffect(() => {
    if (recommended && data.recommended_method !== recommended) {
      setData(prev => ({ ...prev, recommended_method: recommended, threshold_category: thresholdCategory }));
    }
  }, [recommended, thresholdCategory, data.recommended_method]);

  // Which FAR parts apply (badges)
  const badges: string[] = [];
  if (isCommercial) badges.push('FAR 12');
  const method = get('co_selected_method') || recommended;
  if (method.includes('13')) badges.push('FAR 13');
  if (method.includes('14')) badges.push('FAR 14');
  if (method.includes('15')) badges.push('FAR 15');

  // ─── Governing FAR threshold classification (from the backend rules engine) ─
  // Authoritative category + triggers (procedures, competition, TINA). Fetched
  // from the opportunity's saved value/commerciality; refetches when saved.
  const [thr, setThr] = useState<any>(null);
  const loadThresholds = useCallback(async () => {
    try { setThr(await opportunitiesApi.thresholds(oppId)); } catch { setThr(null); }
  }, [oppId]);
  useEffect(() => { loadThresholds(); }, [loadThresholds, data.estimated_value, data.is_commercial, data.commercial_classification]);

  // ─── Primitives ─────────────────────────────────────────────────────────
  const Long = (k: string, l: string, o: { req?: boolean; help?: string } = {}) => (
    <div style={st.field}><label style={st.label}>{l}{o.req && <span style={st.req}> *</span>}</label>
      <textarea style={{ ...st.input, minHeight: 64, resize: 'vertical' }} value={get(k)} onChange={e => set(k, e.target.value)} />
      {o.help && <p style={st.help}>{o.help}</p>}</div>
  );
  const Sel = (k: string, l: string, opts: string[], o: { req?: boolean } = {}) => (
    <div style={st.field}><label style={st.label}>{l}{o.req && <span style={st.req}> *</span>}</label>
      <select style={{ ...st.input, maxWidth: 460 }} value={get(k)} onChange={e => set(k, e.target.value)}>
        <option value="">Select...</option>{opts.map(x => <option key={x} value={x}>{x}</option>)}
      </select></div>
  );
  const YN = (k: string, l: string, opts: string[] = ['Yes', 'No'], o: { req?: boolean } = {}) => (
    <div style={st.field}><label style={st.label}>{l}{o.req && <span style={st.req}> *</span>}</label>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {opts.map(x => <button key={x} type="button" onClick={() => set(k, x)} style={{
          padding: '5px 12px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
          border: `1px solid ${get(k) === x ? '#7c3aed' : '#d1d5db'}`,
          background: get(k) === x ? '#f5f3ff' : 'white', color: get(k) === x ? '#5b21b6' : '#374151',
        }}>{x}</button>)}
      </div></div>
  );

  return (
    <div style={{ maxWidth: 1000 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div>
          <h2 style={{ fontSize: 18, margin: 0 }}>Acquisition Path</h2>
          <p style={{ fontSize: 11, color: '#6b7280', margin: '2px 0 0' }}>Determine commerciality (FAR 12) and select the procedure — Part 13, 14, or 15. Part 12 layers onto whichever applies.</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {dirty && <span style={{ fontSize: 12, color: '#b45309', fontWeight: 600 }}>● Unsaved</span>}
          <button onClick={runAiAssist} disabled={aiLoading} style={{ padding: '10px 18px', background: aiLoading ? '#c4b5fd' : '#7c3aed', color: 'white', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: aiLoading ? 'default' : 'pointer' }}>{aiLoading ? 'Drafting...' : '✨ AI Assist'}</button>
          <button onClick={runAiReview} disabled={reviewLoading} style={{ padding: '10px 18px', background: reviewLoading ? '#bfdbfe' : '#2563eb', color: 'white', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: reviewLoading ? 'default' : 'pointer' }}>{reviewLoading ? 'Reviewing...' : '🔎 AI Review'}</button>
          <button onClick={save} disabled={saving} style={{ padding: '10px 20px', background: saving ? '#86efac' : dirty ? '#7c3aed' : '#10b981', color: 'white', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
      {error && <div role="alert" style={{ padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, color: '#991b1b', fontSize: 13, marginBottom: 12 }}>{error}</div>}
      {message && <div role="status" style={{ padding: '10px 14px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, color: '#166534', fontSize: 13, marginBottom: 12 }}>{message}</div>}
      {aiError && <div role="alert" style={{ padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, color: '#991b1b', fontSize: 13, marginBottom: 12 }}>{aiError}</div>}

      {aiSuggestions && Object.keys(aiSuggestions).length > 0 && (
        <div style={{ background: '#f5f3ff', border: '1px solid #c4b5fd', borderRadius: 8, padding: 16, marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#5b21b6' }}>✨ AI-drafted suggestions (advisory — review before applying)</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={applyAll} style={{ padding: '6px 12px', background: '#7c3aed', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Apply all</button>
              <button onClick={() => setAiSuggestions(null)} style={{ padding: '6px 12px', background: 'white', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>Dismiss</button>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {ACQ_AI_FIELDS.filter(f => aiSuggestions[f.key]).map(f => (
              <div key={f.key} style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 6, padding: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: '#374151' }}>{f.label}</span>
                  <button onClick={() => { set(f.key, aiSuggestions[f.key]); setAiSuggestions(prev => { if (!prev) return prev; const n = { ...prev }; delete n[f.key]; return n; }); }} style={{ padding: '3px 10px', background: '#ede9fe', color: '#5b21b6', border: '1px solid #c4b5fd', borderRadius: 4, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>Apply</button>
                </div>
                <div style={{ fontSize: 12, color: '#4b5563', whiteSpace: 'pre-wrap' }}>{String(aiSuggestions[f.key])}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {review && (
        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: 16, marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#1e40af' }}>🔎 AI Compliance Review (advisory)</div>
            <button onClick={() => setReview(null)} style={{ padding: '4px 10px', background: 'white', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>Dismiss</button>
          </div>
          {review.summary && <p style={{ fontSize: 12, color: '#1e3a8a', margin: '0 0 12px', fontStyle: 'italic' }}>{review.summary}</p>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#92400e', marginBottom: 6 }}>GAPS ({review.gaps.length})</div>
              {review.gaps.length === 0 ? <div style={{ fontSize: 11, color: '#9ca3af' }}>None flagged</div> :
                <ul style={{ margin: 0, paddingLeft: 16, fontSize: 11, color: '#78350f' }}>{review.gaps.map((g, i) => <li key={i} style={{ marginBottom: 3 }}>{g}</li>)}</ul>}
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#991b1b', marginBottom: 6 }}>COMPETITION / COMPLIANCE RISKS ({review.risks.length})</div>
              {review.risks.length === 0 ? <div style={{ fontSize: 11, color: '#9ca3af' }}>None flagged</div> :
                <ul style={{ margin: 0, paddingLeft: 16, fontSize: 11, color: '#b91c1c' }}>{review.risks.map((r, i) => <li key={i} style={{ marginBottom: 3 }}>{r}</li>)}</ul>}
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#5b21b6', marginBottom: 6 }}>INCONSISTENCIES ({review.inconsistencies.length})</div>
              {review.inconsistencies.length === 0 ? <div style={{ fontSize: 11, color: '#9ca3af' }}>None flagged</div> :
                <ul style={{ margin: 0, paddingLeft: 16, fontSize: 11, color: '#6d28d9' }}>{review.inconsistencies.map((c, i) => <li key={i} style={{ marginBottom: 3 }}>{c}</li>)}</ul>}
            </div>
          </div>
        </div>
      )}

      {/* Commerciality */}
      <div style={st.section}>
        <h3 style={st.title}>Commerciality Determination</h3>
        {YN('is_commercial', 'Is the requirement for a commercial product or commercial service?', ['Yes', 'No', 'Determination Required'], { req: true })}
        {Sel('commercial_classification', 'Commercial Classification', ['Commercial product', 'Commercial service', 'Commercially available off-the-shelf item', 'Nondevelopmental item', 'Noncommercial product or service', 'Undetermined'])}
        <div style={st.field}>
          <label style={st.label}>Estimated Total Acquisition Value Including Options<span style={st.req}> *</span></label>
          <input style={{ ...st.input, maxWidth: 260 }} value={get('estimated_value')} onChange={e => set('estimated_value', e.target.value)} placeholder="$ e.g., 5,000,000" />
        </div>
        {Long('commerciality_determination', 'Commerciality Determination', { help: 'Explain the basis for the commercial (or noncommercial) determination.' })}
      </div>

      {/* Competition & discussions */}
      <div style={st.section}>
        <h3 style={st.title}>Competition & Method Drivers</h3>
        {YN('price_only', 'Will award be based on price and price-related factors only (no tradeoffs)?', ['Yes', 'No'])}
        {YN('discussions_needed', 'Are discussions / negotiations with offerors needed?', ['Yes', 'No', 'To Be Determined'])}
        {YN('nonprice_important', 'Are non-price (technical/past-performance) factors important to the award?', ['Yes', 'No'])}
        {YN('multiple_sources', 'Is more than one responsive offer reasonably expected?', ['Yes', 'No', 'Unknown'])}
      </div>

      {/* System recommendation */}
      <div style={{ ...st.section, background: '#f5f3ff', border: '1px solid #c4b5fd' }}>
        <h3 style={st.title}>System Recommendation</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 12 }}>
          <div><div style={st.label}>Applicable Threshold Category</div><div style={{ fontSize: 13, color: '#1f2937' }}>{thresholdCategory || '—'}</div></div>
          <div><div style={st.label}>Recommended Solicitation Method</div><div style={{ fontSize: 13, fontWeight: 600, color: '#5b21b6' }}>{recommended || 'Enter an estimated value'}</div></div>
        </div>
        {badges.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            {badges.map(b => <span key={b} style={{ padding: '3px 10px', background: '#dcfce7', color: '#166534', borderRadius: 9999, fontSize: 11, fontWeight: 600, marginRight: 6 }}>{b} Applicable</span>)}
          </div>
        )}
        {Sel('co_selected_method', "Contracting Officer's Selected Method", ['FAR Part 13 — Simplified Acquisition', 'FAR Part 14 — Sealed Bidding', 'FAR Part 15 — Contracting by Negotiation'], { req: true })}
        {Long('selection_rationale', 'Selection Rationale', { req: true })}
        {get('price_only') === 'No' && get('co_selected_method')?.includes('14') && (
          <div style={{ padding: '8px 12px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, fontSize: 12, color: '#92400e' }}>
            Warning: Sealed bidding (Part 14) is generally not appropriate when discussions or non-price tradeoffs are required. Consider Part 15.
          </div>
        )}
      </div>

      {/* Governing FAR threshold detail (from the backend rules engine) */}
      {thr && thr.category && thr.category !== 'unknown' && (
        <div style={st.section}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{ fontSize: 16 }} aria-hidden="true">📏</span>
            <h3 style={{ ...st.title, margin: 0 }}>Acquisition Threshold</h3>
            <span style={{ padding: '2px 10px', background: '#e0e7ff', color: '#4338ca', borderRadius: 9999, fontSize: 11, fontWeight: 700 }}>{thr.category_label}</span>
          </div>
          <p style={{ fontSize: 12, color: '#374151', margin: '4px 0 10px' }}>{thr.headline}</p>

          {Array.isArray(thr.procedures) && thr.procedures.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <div style={st.label}>Applicable procedures</div>
              {thr.procedures.map((p: string, i: number) => (
                <div key={i} style={{ fontSize: 12, color: '#1f2937' }}>• {p}</div>
              ))}
            </div>
          )}
          {Array.isArray(thr.triggers) && thr.triggers.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <div style={st.label}>What this triggers</div>
              {thr.triggers.map((t: string, i: number) => (
                <div key={i} style={{ fontSize: 12, color: '#374151', lineHeight: 1.5 }}>• {t}</div>
              ))}
            </div>
          )}
          <div style={{ fontSize: 12, marginBottom: 8 }}>
            <span style={st.label}>Certified cost or pricing data (TINA)</span>
            <span style={{ fontWeight: 600, color: thr.tina_certified_cost_pricing ? '#b45309' : '#166534' }}>
              {thr.tina_certified_cost_pricing ? 'Likely required (over $2M, non-commercial) — verify exceptions' : 'Not indicated at this value/commerciality'}
            </span>
          </div>
          <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 4 }}>
            {thr.note} Reflects the last saved estimated value & commerciality.
          </div>
        </div>
      )}

      {/* FAR Part 12 form — shown when the acquisition is commercial */}
      {isCommercial && <Far12Form get={far12Get} set={far12Set} />}

      {/* FAR Part 13 form — shown when the selected method is Part 13 */}
      {method.includes('13') && <Far13Form get={far13Get} set={far13Set} />}

      {/* FAR Part 14 form — shown when the selected method is Part 14 */}
      {method.includes('14') && <Far14Form get={far14Get} set={far14Set} />}

      {/* FAR Part 15 form — shown when the selected method is Part 15 */}
      {method.includes('15') && <Far15Form get={far15Get} set={far15Set} />}

      {/* Next steps */}
      <div style={st.section}>
        <h3 style={st.title}>Next Steps</h3>
        <p style={st.sub}>Continue to the workflow sub-tabs above. They adapt to your selected method{isCommercial ? ' and include FAR Part 12 commercial terms' : ''}.</p>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button onClick={() => { save(); goToSubTab?.('draft'); }} style={{ padding: '10px 16px', background: '#eef2ff', color: '#4338ca', border: '1px solid #c7d2fe', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>2. Solicitation Development →</button>
          <button onClick={() => { save(); goToSubTab?.('matches'); }} style={{ padding: '10px 16px', background: '#eef2ff', color: '#4338ca', border: '1px solid #c7d2fe', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>3. Responses & Evaluation →</button>
          <button onClick={() => { save(); goToSubTab?.('evaluate'); }} style={{ padding: '10px 16px', background: '#eef2ff', color: '#4338ca', border: '1px solid #c7d2fe', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>4. Selection & Award →</button>
        </div>
      </div>
    </div>
  );
}
